/**
 * Road Snap Utility — snaps GPS coordinates to the nearest road via OSRM Match API.
 * Uses a backend proxy (/api/snap-to-road) to keep the architecture clean.
 */

// Round to 5 decimal places (~1m precision)
const roundCoord = (v) => Math.round(v * 1e5) / 1e5;

function normalizePoints(points) {
  if (!Array.isArray(points)) return [];
  const normalized = [];
  for (const point of points) {
    if (!point || point.lat == null || point.lng == null) continue;
    const lat = roundCoord(point.lat);
    const lng = roundCoord(point.lng);
    const prev = normalized[normalized.length - 1];
    if (!prev || prev.lat !== lat || prev.lng !== lng) {
      const p = { lat, lng };
      if (point.ts != null) p.ts = point.ts;
      normalized.push(p);
    }
  }
  return normalized;
}

/**
 * Snap an array of GPS points to the nearest road.
 * @param {Array<{lat: number, lng: number}>} points - 2+ GPS coordinates
 * @param {object} api - Axios instance with auth headers
 * @returns {Promise<Array<{lat: number, lng: number}>|null>} Snapped road path, or null on failure
 */
export async function snapToRoad(points, api) {
  const result = await snapToRoadDetailed(points, api);
  if (!result) return null;
  return result.snapped;
}

export async function snapToRoadDetailed(points, api) {
  if (!points || points.length < 2 || !api) {
    return { snapped: null, diagnostics: { error: 'insufficient_points' } };
  }

  const normalizedPoints = normalizePoints(points);
  if (normalizedPoints.length < 2) {
    return { snapped: null, diagnostics: { error: 'insufficient_points' } };
  }

  try {
    const resp = await api.post('/snap-to-road', { points: normalizedPoints });
    const snapped = resp.data?.snappedPoints;
    if (snapped && snapped.length >= 2) {
      return {
        snapped,
        diagnostics: {
          inputCount: normalizedPoints.length,
          outputCount: snapped.length,
          fallback: resp.data?.fallback || false,
          error: resp.data?.error || resp.data?.providerError || null,
        },
      };
    }
    return { snapped: null, diagnostics: { error: resp.data?.error || 'no_snapped_points' } };
  } catch {
    return { snapped: null, diagnostics: { error: 'request_failed' } };
  }
}

/**
 * Compute cumulative distances along a waypoint polyline.
 */
export function computePathMetrics(waypoints) {
  if (!waypoints || waypoints.length < 2) return { totalDistance: 0, cumulativeDistances: [0] };
  const cumDist = [0];
  let total = 0;
  for (let i = 1; i < waypoints.length; i++) {
    const dlat = waypoints[i].lat - waypoints[i - 1].lat;
    const dlng = waypoints[i].lng - waypoints[i - 1].lng;
    total += Math.sqrt(dlat * dlat + dlng * dlng);
    cumDist.push(total);
  }
  return { totalDistance: total, cumulativeDistances: cumDist };
}

/**
 * Interpolate a position along a waypoint polyline at a given progress (0-1).
 */
export function interpolateAlongPath(waypoints, cumulativeDistances, totalDistance, progress) {
  if (!waypoints || waypoints.length < 2 || totalDistance === 0) return null;

  const targetDist = Math.min(Math.max(progress, 0), 1) * totalDistance;

  let segIdx = 0;
  for (let i = 1; i < cumulativeDistances.length; i++) {
    if (cumulativeDistances[i] >= targetDist) {
      segIdx = i - 1;
      break;
    }
    segIdx = i - 1;
  }

  const segStart = cumulativeDistances[segIdx];
  const segEnd = cumulativeDistances[segIdx + 1] ?? totalDistance;
  const segLen = segEnd - segStart;
  const t = segLen > 0 ? (targetDist - segStart) / segLen : 0;

  const p1 = waypoints[segIdx];
  const p2 = waypoints[segIdx + 1] || waypoints[segIdx];

  const lat = p1.lat + (p2.lat - p1.lat) * t;
  const lng = p1.lng + (p2.lng - p1.lng) * t;

  const dLng = (p2.lng - p1.lng) * Math.PI / 180;
  const lat1r = p1.lat * Math.PI / 180;
  const lat2r = p2.lat * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2r);
  const x = Math.cos(lat1r) * Math.sin(lat2r) - Math.sin(lat1r) * Math.cos(lat2r) * Math.cos(dLng);
  const heading = ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;

  return { lat, lng, heading };
}

export function clearSnapCache() {
  // No-op — cache removed for performance
}
